/**
 * Hotel Thank You API
 *
 * Handles hotel thank you (booking confirmation) API calls.
 */
import type {
  HotelThankYouResponse,
  HotelThankYouCBPayload,
  CBUrlParamsFull,
  CBBaseHeaders,
} from '@contracts';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { buildUrl, buildHeaders } from '@/api/utils';
import { generateUUID } from '@/shared/utils';
import { getAllMMTCookies } from '@/shared/utils/appCookies';
import { logger } from '@/shared/utils/logger';

// ============================================
// Error Class
// ============================================

export class HotelThankYouError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'HotelThankYouError';
  }
}

// ============================================
// Types
// ============================================

interface ApiErrorInfo {
  code: string;
  message: string;
}

export interface HotelThankYouRequestParams {
  sessionId: string;
  userId: string;
  payload: HotelThankYouCBPayload;
}

export interface HotelThankYouApiResponse {
  type: 'hotels-thankyou';
  data: HotelThankYouResponse | null;
  success: boolean;
  error?: ApiErrorInfo;
}

interface RawResponse {
  type?: string;
  success?: boolean;
  data?: HotelThankYouResponse | null;
  error?: ApiErrorInfo;
  bookingStatus?: HotelThankYouResponse['bookingStatus'];
  bookingMeta?: HotelThankYouResponse['bookingMeta'];
  hotel?: HotelThankYouResponse['hotel'];
  bookingDetails?: HotelThankYouResponse['bookingDetails'];
  guestDetails?: HotelThankYouResponse['guestDetails'];
  amountPaid?: HotelThankYouResponse['amountPaid'];
  footerLinks?: HotelThankYouResponse['footerLinks'];
  ctas?: HotelThankYouResponse['ctas'];
}

// ============================================
// Helper Functions
// ============================================

function getCBContext() {
  const cookies = getAllMMTCookies();

  return {
    language: cookies.language ?? 'eng',
    currency: cookies.currency ?? 'INR',
    countryCode: cookies.countryCode ?? 'IN',
    region: (cookies.countryCode ?? 'IN').toLowerCase(),
    authToken: cookies.auth ?? '',
    uuid: cookies.uuid ?? '',
    deviceId: cookies.deviceId ?? '',
  };
}

function buildCBUrlParamsFull(requestId: string): CBUrlParamsFull {
  const { language, currency, countryCode, region } = getCBContext();
  return {
    requestId,
    language,
    region,
    currency,
    idContext: 'B2C',
    countryCode,
  };
}

function buildCBHeaders(): CBBaseHeaders {
  const { currency, language, authToken, uuid, deviceId } = getCBContext();
  return {
    'content-type': 'application/json',
    currency,
    language,
    'X-Gommt-Mmt-Auth': authToken,
    'X-Gommt-Uuid': uuid,
    'X-Device-Id': deviceId,
  };
}

export function buildThankYouCBPayload(txnKey: string): HotelThankYouCBPayload {
  const requestId = generateUUID();

  return {
    urlParams: buildCBUrlParamsFull(requestId),
    headers: buildCBHeaders(),
    requestBody: {
      txnKey,
    },
  };
}

// ============================================
// API Function
// ============================================

export async function fetchHotelThankYou(
  params: HotelThankYouRequestParams,
): Promise<HotelThankYouApiResponse> {
  const url = buildUrl(HOTELS_ENDPOINTS.THANK_YOU, {
    sessionId: params.sessionId,
    userId: params.userId,
  });

  logger.warn('[HotelThankYouClient] Fetching:', { sessionId: params.sessionId });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(params.payload),
    });

    const data = (await response.json()) as RawResponse | null;

    if (!response.ok) {
      logger.error('[HotelThankYouClient] API returned error status:', response.status, data);
      if (data?.error) {
        return { type: 'hotels-thankyou', success: false, data: null, error: data.error };
      }
      return {
        type: 'hotels-thankyou',
        success: false,
        data: null,
        error: { code: 'HTTP_ERROR', message: `HTTP ${response.status}` },
      };
    }

    // API returned error
    if (data?.success === false && data?.error) {
      logger.warn('[HotelThankYouClient] API returned error:', data.error);
      return { type: 'hotels-thankyou', success: false, data: null, error: data.error };
    }

    // CBackend returns the transformed response directly
    if (
      data?.bookingStatus &&
      data.bookingMeta &&
      data.hotel &&
      data.bookingDetails &&
      data.guestDetails &&
      data.amountPaid &&
      data.footerLinks
    ) {
      const thankYouData: HotelThankYouResponse = {
        bookingStatus: data.bookingStatus,
        bookingMeta: data.bookingMeta,
        hotel: data.hotel,
        bookingDetails: data.bookingDetails,
        guestDetails: data.guestDetails,
        amountPaid: data.amountPaid,
        footerLinks: data.footerLinks,
        ctas: data.ctas,
      };
      logger.warn('[HotelThankYouClient] Success (direct response)');
      return { type: 'hotels-thankyou', success: true, data: thankYouData };
    }

    // Wrapped response format
    if (data?.data) {
      logger.warn('[HotelThankYouClient] Success (wrapped response)');
      return { type: 'hotels-thankyou', success: true, data: data.data };
    }

    // No data
    logger.warn('[HotelThankYouClient] Empty response');
    return {
      type: 'hotels-thankyou',
      success: false,
      data: null,
      error: { code: 'EMPTY_RESPONSE', message: 'Empty response from server' },
    };
  } catch (error) {
    logger.error('[HotelThankYouClient] Request failed:', error);
    return {
      type: 'hotels-thankyou',
      success: false,
      data: null,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network request failed',
      },
    };
  }
}
